/**
 * Client for the feat-053c Training Progress endpoint (routes/training_progress.py).
 *
 * Contracts:
 *  - Spec:    worktrees/feat-053c-training/spec.md
 *  - ADR 0001 (decisions/0001-training-progress.md) — D1 aggregate read shape,
 *    D2 status rule (over REAL columns only), D3 placeholder excuse contract.
 *
 * THE HONESTY CRUX (ADR D3): the REAL columns (units, recorded, worked, accuracy,
 * last_active) are real backend sources; `status` is derived from the REAL
 * counters ONLY and is NEVER mutated by an excuse. The excuse/override layer
 * (`times_excused`, `excuse_history`) is PLACEHOLDER — each row carries
 * `excuse_placeholder: true` + `excuse_data_source: "seeded_excuse"`. The Slate
 * `preview-*` EstimatedMarker (reused from feat-053b) attaches to the excuse
 * surfaces (Times-excused count, the "Excused (sample)" qualifier, the modal
 * history panel) so a manager never reads a placeholder excuse as a real record.
 *
 * The frontend NEVER recomputes a metric (feedback_metrics_single_source): it
 * renders server values + server-formatted `*_display` strings verbatim and reads
 * the server-stamped flags. The manager-override WRITE is DISPLAY-ONLY this phase
 * (a disabled "Override · coming soon" chip) — no persist endpoint exists.
 *
 * Fake mode (`config.environment === 'fake'`, npm run start:fake): returns a
 * deterministic canned roster in the same shape so the whole screen renders with
 * no backend. The contract is the real contract — feat-053e swaps the seeded
 * excuse layer for a real table with NO frontend reshape.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

// ---------- Domain types (mirror ADR D1 / backend TrainingProgressResponse) ----------

// feat-112: status now reflects the rolling-14 window (same window as the Lesson %/
// Recorded % columns), not all-time totals — so it is recoverable. `no_recent_shifts`
// is the neutral state for crew with ZERO completed Square workdays in the window:
// the pace rule would vacuously yield `on_track`, so it is short-circuited to a grey,
// non-judgmental badge that is EXCLUDED from the Behind / On track / Above buckets.
// feat-195 D2: an exhausted member (units >= units_total > 0) still reports one of
// these FOUR. Exhaustion is a MODIFIER carried on `status_reason.lessons_exhausted`,
// NOT a fifth member (ADR 0001 §B7) — do not add one.
export type TrainingStatus = 'behind' | 'on_track' | 'above' | 'no_recent_shifts';

// ---------- feat-195: the status-reason payload (mirror ADR 0001 Decision B) ----------
// THE RULE THIS TYPE EXISTS TO ENFORCE (spec D6): the backend produces every number
// and every sentence; React renders them VERBATIM and performs ZERO threshold
// arithmetic outside fake mode (AC4 is a grep of the diff). A TypeScript
// re-derivation would silently drift from the real verdict, which is the exact
// failure this feature exists to prevent.

/**
 * One row of the card's breakdown table. The renderer ITERATES `metrics[]` and
 * knows nothing about what a lesson is (ADR §B2) — it must never branch per metric.
 *
 * The three nullable fields answer DIFFERENT questions and must not be collapsed
 * (ADR §B6a):
 *   • `value`     null ⇒ this number is not meaningful to display → render "—".
 *   • `threshold` null ⇒ there is no numeric line → render no "needs N". TWO causes
 *                        (not counted / no minimum exists) that are deliberately
 *                        INDISTINGUISHABLE to the renderer; the backend-owned `note`
 *                        carries the human-facing difference.
 *   • `meets`     null ⇒ this axis is not being judged. Binds to `counted`, NOT to
 *                        `threshold` — at worked_days == 1 the grace line is 0, so the
 *                        axis IS counted and IS trivially cleared and keeps its "OK".
 */
export interface StatusReasonMetric {
  key: string; // "worked_days" | "lessons" | "recorded_days" — stable styling hook
  label: string; // backend-owned display text, e.g. "Days worked (Square)"
  // AMENDMENT 1 (ADR §B6a, Preston's O1): a REQUIRED property with a NULLABLE type.
  // NEVER `value?:` — absence must be unrepresentable at compile time, because two
  // ways to express one thing is how the renderer ends up branching, and a branch is
  // arithmetic's doorway. `value === null → "—"` is the ONLY rule; the frontend may
  // NOT decide to draw an em-dash for a `0` it was handed (that is a display decision,
  // and display decisions the frontend makes are what this contract exists to prevent).
  value: number | null;
  threshold: number | null;
  meets: boolean | null;
  counted: boolean; // false ONLY when the D2 carve-out actually fired
  note: string | null; // short backend-owned qualifier, rendered VERBATIM
}

export interface StatusReasonWindow {
  start: string; // "YYYY-MM-DD" inclusive
  end_exclusive: string; // "YYYY-MM-DD" EXCLUSIVE — NEVER render as a day (feat-095)
  // The complete header line, e.g. "Last 14 days (Jul 16 – Jul 29)". The backend emits
  // the WHOLE string precisely so the end_exclusive − 1 day subtraction never happens
  // in TypeScript (ADR §B3). Per-row, because the window is per-LOCATION.
  display: string;
}

export interface StatusReason {
  status: string; // echo of row.status — duplicated ON PURPOSE (ADR §B4) so a
  // pill/card divergence is DETECTABLE rather than silent. QA asserts
  // row.status === row.status_reason.status. The card header still keys off
  // row.status; this echo exists to be checked, not to be rendered.
  window: StatusReasonWindow;
  metrics: StatusReasonMetric[]; // display order: worked_days, lessons, recorded_days
  triggered_by: string[]; // metric keys; [] for on_track / no_recent_shifts
  grace_days: number; // 1
  lessons_exhausted: boolean; // D2 modifier — see TrainingStatus above
  // Each element is a COMPLETE, GRAMMATICAL SENTENCE with its numbers already inside.
  // The frontend may join them with a LINE BREAK ONLY and may NEVER insert,
  // interpolate, reformat or re-order them — ARRAY ORDER **IS** RENDER ORDER
  // (ADR §B1 / §B6b). "Which sentence wins" is a copy decision the backend owns.
  sentences: string[];
}

export interface TrainingExcuseEntry {
  reason: string;
  manager_name: string;
  date: string; // "Jun 6, 2026"
  base_units: number;
  base_recorded: number;
  base_worked: number;
}

export interface TrainingProgressRow {
  server_id: string;
  name: string;
  initials: string;
  location_id: string;
  location_name: string; // REAL — backend location.name for the crew member (feat-053f F1)
  // ── REAL columns ──
  units: number;
  /** feat-302 (ADR 0001 §5, spec Amendment 7): PER-LEARNER — the Lessons that
   *  apply to THIS member by audience scope. Any "N of M" for a row must use this,
   *  never the response's top-level `units_total`. */
  units_total: number;
  recorded: number; // feat-177: distinct location-local DAYS (all-time) with ≥1 recorded shift — two shifts in one day count once, NOT a shift count
  worked: number; // feat-084 #1/D2: now distinct location-local days worked (all-time), == days_worked_all_time
  days_worked_all_time: number; // feat-084 D2: named alias of the displayed `worked` day-count
  // ── feat-105: last-2-weeks TOTALS-basis adherence (ADR 0001, option (b)) ──
  // The backend now returns the FINAL pre-capped percentage; the frontend is a
  // pure formatter and does ZERO arithmetic (no num/den). lesson_pct/recorded_pct
  // are already rounded and server-final, with `null` for a ratio that is not
  // meaningful on this row (→ render "—").
  // feat-195 AC12 — THIS COMMENT USED TO NAME ONE CAUSE ("no worked days in the
  // window") AND THAT IS NOW FALSE. `lesson_pct` has TWO independent null causes:
  //   (1) no worked days in the window — the shared `_window_pct` den==0 sentinel; and
  //   (2) D8: the member has completed EVERY available lesson (units >= units_total > 0),
  //       so the ratio is not meaningful for them even though it computes to 0.
  // The old invariant that the neutral `no_recent_shifts` badge and the "—" cells "land
  // on EXACTLY the same rows" is therefore BROKEN BY DESIGN — an exhausted row renders
  // "—" for Lesson % while carrying a judgmental pill, and the status hover card carries
  // the reason. `recorded_pct` keeps cause (1) only. Dated 2026-07-29.
  // lessons_completed_in_window /
  // recorded_days_in_window are the raw in-window numerators — they ride for
  // transparency/reconciliation (ADR 0001 D3) and are NOT rendered as columns.
  // feat-177: the two are NOT the same kind of number any more, and their CAPS differ.
  // Lessons is a MULTISET count (two lessons in one day count twice) and stays CAPPED at
  // 100 — an uncapped lesson ratio would be meaningless. The recorded numerator is a
  // plain DISTINCT-DAY count and is UNCAPPED: recorded days are not a subset of Square
  // worked days, so `recorded_pct` legitimately exceeds 100 and must render that way.
  worked_days_in_window: number; // denominator (rolling 14d, location-local); also the pct denominator + a reconciliation input
  lesson_pct: number | null; // LESSON % (last 2 weeks), server-final, CAPPED 0..100; null ⇒ render "—"
  recorded_pct: number | null; // RECORDED % (last 2 weeks), server-final, UNCAPPED — feat-177: may exceed 100; null ⇒ render "—"
  lessons_completed_in_window: number; // raw numerator (multiset): unit completions in the rolling-14 window
  recorded_days_in_window: number; // feat-177 raw numerator (DISTINCT DAYS, uncapped): rolling-14 days with ≥1 recorded shift, clock day or not
  // ── feat-084 Phase-2 (ADR 0012a A3/Slice B): all-time analogs of the two
  // same-day numerators. These are STATUS INPUTS the backend's days-basis
  // derive_status consumes; the frontend does not render them (no new column) and
  // never re-derives status — they ride the response for type parity / transparency.
  worked_days_with_lesson_same_day_all_time: number; // distinct worked days (all-time) with a same-day lesson
  worked_days_with_recording_same_day_all_time: number; // distinct worked days (all-time) with a same-day recording
  accuracy: number | null; // null → no scored shifts in window
  accuracy_display: string; // "96%" | "—"
  last_active: string | null; // ISO
  last_active_display: string; // "Today, 8:30 AM" | "5 days ago"
  // ── derived over REAL columns (D2) — NEVER excuse-mutated ──
  status: TrainingStatus;
  // feat-195: the backend's explanation of THIS row's verdict. Optional on the wire
  // (ADR §B5) — not for old clients but for the DEPLOY WINDOW, where a new frontend
  // briefly receives rows from a backend that does not emit it yet. When absent the
  // Status pill renders EXACTLY as it does today: no hover card, and no affordance at
  // all (design brief §1.5 — a focusable chip that opens an empty card advertises an
  // explanation and then fails to deliver one).
  status_reason?: StatusReason | null;
  // ── PLACEHOLDER layer (D3) ──
  times_excused: number; // == excuse_history.length
  excuse_history: TrainingExcuseEntry[]; // most-recent-first
  excuse_placeholder: boolean; // always true this phase
  excuse_data_source: string; // "seeded_excuse"
}

export interface TrainingProgressSummary {
  // feat-112: the three judgmental buckets now EXCLUDE no_recent_shifts crew.
  on_track: number; // count(status NOT IN behind, no_recent_shifts) — still includes "above"
  behind: number;
  above: number;
  no_recent_shifts: number; // feat-112: crew with 0 completed Square shifts in the rolling-14 window
  recorded_vs_worked_pct: number;
  crew_count: number;
}

export interface TrainingProgressResponse {
  rows: TrainingProgressRow[];
  summary: TrainingProgressSummary;
  /** The TENANT-WIDE catalog size. feat-302: no longer any member's denominator
   *  once audience scoping is on — render a row's own `units_total` instead. */
  units_total: number;
  placeholder: boolean; // real columns are real → false
  data_source: string; // "real"
}

// ---------- feat-095: per-day recent-activity timeline (mirror ADR 0017 D1) ----------
// The crew-member dialog hits GET /api/training-progress/{tenant}/{server}/activity
// when it opens (lazy, not bundled into the roster). The feed surfaces the SAME
// per-day date sets that power the roster's Lesson % / Recorded % columns, so a
// manager can reconstruct the 67%. React renders the backend flags/titles verbatim
// — NO bucketing or %/day math here (feedback_metrics_single_source).

export interface ActivityLesson {
  // `lesson_id` is the LEVEL-1 (top-level) curriculum id, and `title` is that
  // row's title. SEMANTICS = one level-1 item completed, dated to the day its
  // last required child was done.
  //
  // feat-198 (ADR 0001) DELIBERATELY DOES NOT TOUCH THESE TWO KEYS, and this is
  // the one place in the payload where that is true. Before the rename the name
  // was a compromise — ADR 0003's "lesson == unit", i.e. the key said "lesson"
  // while the backend called level 1 a `unit`. The rename made level 1 the
  // product's Lesson everywhere, so `lesson_id` stopped being a continuity
  // shim and became simply correct. Every neighbouring key that says "lesson"
  // moved to "drill" because it held LEVEL-2 data; this one did not, because it
  // never did. A mechanical lesson_→drill_ sweep flips it and is wrong.
  lesson_id: string;
  title: string;
}

export interface ActivityDay {
  date: string; // "YYYY-MM-DD", location-local calendar date
  worked: boolean; // location-local date ∈ the server's Square worked-day set
  recorded: boolean; // a Bespoke shift had ≥1 transcript_part that local date
  lessons: ActivityLesson[]; // UNIT(s) completed that date (may be empty)
}

export interface TrainingActivityResponse {
  server_id: string;
  days: ActivityDay[]; // newest-first, activity-only (zero-activity days omitted)
  window_start: string; // "YYYY-MM-DD" inclusive (= today_local - 13)
  window_end: string; // "YYYY-MM-DD" EXCLUSIVE (= today_local + 1) — never render as a day
}

// ---------- HTTP plumbing (mirrors pilotDashboardService) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

/** Raised when the tenant is unreachable (fail-closed 404 — no existence leak). */
export class TrainingProgressForbiddenError extends Error {
  constructor() {
    super('Training progress is not available for this tenant');
    this.name = 'TrainingProgressForbiddenError';
  }
}

async function api<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 404 || resp.status === 403) {
    // Fail-closed tenant scoping (ADR D1 / D4): treat as "not available".
    throw new TrainingProgressForbiddenError();
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

const isFake = (): boolean => config.environment === 'fake';

// ---------- Fake-mode deterministic data (matches the export + mockup) ----------
// Real columns mirror the export's CREW; the excuse layer mirrors the export's
// SEED (Carlos: 1 entry, Ana: 2 entries) — every excuse row is flagged
// placeholder so the Slate marker renders, and the status is computed from the
// REAL counters only (an excuse never flips it). "behind-but-excused" (Carlos)
// stays "behind" with the Slate "Excused (sample)" qualifier beside it.

interface FakeSeed {
  server_id: string;
  name: string;
  initials: string;
  location_id: string;
  location_name: string;
  units: number;
  recorded: number;
  worked: number; // distinct days worked, all-time (feat-084)
  // feat-105 last-2-weeks TOTALS-basis raw inputs: the denominator (worked days
  // in window) + the two in-window multiset numerators (totals, NOT same-day).
  // fakeRow derives lesson_pct/recorded_pct from these via windowPct (the fake
  // mode is the backend stand-in, so the cap+round math lives in the builder).
  worked_days_in_window: number;
  lessons_completed_in_window: number;
  recorded_days_in_window: number;
  // Optional override for the ALL-TIME recorded∩worked intersection (the row's
  // `worked_days_with_recording_same_day_all_time`, a feat-084 Phase-2 STATUS input —
  // NOT the tile numerator; the tile is sum(recorded)/sum(worked)). Defaults to
  // min(recorded, worked), which is only right when every recorded day was a clock
  // day. Seed it explicitly for anyone who records off the clock (s-nk), otherwise the
  // default would claim they recorded on every single day they worked.
  worked_days_with_recording_all_time?: number;
  accuracy: number;
  last_active_display: string;
  excuses: Array<{ reason: string; manager_name: string; date: string; base_units: number; base_recorded: number; base_worked: number }>;
}

// feat-195: raised 10 → 12 so the D2 carve-out fires ONLY on the seeds that opt into
// it. At 10, six pre-existing seeds carried `units: 10` and would have been swept into
// the exhausted population by accident — flipping their Lesson % to "—" and, worse,
// leaving them internally incoherent (s-dc claims 12 windowed lesson completions, which
// is impossible when only 10 units exist). 12 also matches design-lead's copy deck,
// whose curriculum clause reads "All 12 lessons in the curriculum are complete".
// The exhausted fixtures below (s-tv, s-rh, s-og) set `units: 12` deliberately.
// Typed `number`, not inferred as the literal 12: the singular-catalog branch in the
// copy mirror (`units_total === 1` → "The one lesson…") is part of the backend rule and
// must stay written down, and a literal type would make TS reject it as dead.
const FAKE_UNITS_TOTAL: number = 12;

/**
 * Stable tenant-locations list for fake mode (feat-053f F3). The page's location
 * filter populates from THIS, not from `data.rows` — rows collapse to one location
 * once filtered, so a row-derived list would lose its other options. Mirrors the
 * shape `managementService.getLocationsByTenant` returns in real mode ({id, name}).
 */
export const FAKE_TRAINING_LOCATIONS: Array<{ id: string; name: string }> = [
  { id: 'loc-downtown', name: 'Downtown' },
  { id: 'loc-airport', name: 'Airport' },
];

// feat-105: each seed carries last-2-weeks TOTALS-basis inputs —
//   worked_days_in_window (denominator), lessons_completed_in_window and
//   recorded_days_in_window (the in-window numerators, NOT same-day pairs; feat-177:
//   lessons is a multiset, recorded_days is a distinct-DAY count).
//   fakeRow derives lesson_pct = min(100, round(L/W*100)) (and recorded likewise)
//   via windowPct, or null when worked_days_in_window === 0 (Priya, a recent hire
//   with no shifts in the window → both columns render "—").
//   feat-177: the two numerators no longer behave alike, and the seeds must show it.
//   `lessons_completed_in_window` is still a MULTISET and may EXCEED the denominator
//   (multiple lessons in one day) — s-jr is that fixture, and it is why the cap still
//   exists — and lessons stays CAPPED at 100 for that reason.
//   The recorded numerator is a plain distinct-DAY count and is UNCAPPED. Recorded days
//   are NOT a subset of Square worked days, so both `recorded > worked` (the column) and
//   `recorded_pct > 100` (the %) are legal shapes; s-nk is seeded to exercise both
//   (AC7a). Do not "restore" a recorded ≤ worked invariant and do not re-cap the
//   recorded side — an earlier revision of this spec intersected the numerator to force
//   ≤100 and that was REJECTED (it zeroed out servers with no Square mapping).
//   Emma Rocelis Amaya Perez is the long-name alignment fixture.
const FAKE_CREW: FakeSeed[] = [
  { server_id: 's-dc', name: 'Daniel Cruz', initials: 'DC', location_id: 'loc-downtown', location_name: 'Downtown', units: 10, recorded: 10, worked: 10, worked_days_in_window: 12, lessons_completed_in_window: 12, recorded_days_in_window: 12, accuracy: 96, last_active_display: 'Today, 8:30 AM', excuses: [] },
  // feat-177: Maria's recorded numerator was 12 over 11 worked days — the old
  // multiset "recorded more shifts than days worked" shape, now impossible. She
  // records on every one of her 11 worked days (11/11 → 100%) and reads on_track;
  // the Above & beyond fixture is s-jr, whose LESSON multiset still exceeds.
  { server_id: 's-mg', name: 'Maria Gonzalez', initials: 'MG', location_id: 'loc-downtown', location_name: 'Downtown', units: 10, recorded: 10, worked: 10, worked_days_in_window: 11, lessons_completed_in_window: 11, recorded_days_in_window: 11, accuracy: 94, last_active_display: 'Today, 8:12 AM', excuses: [] },
  // Lucia: 8 lessons + 6 recordings over 9 worked days → 89% / 67% (distinct values).
  { server_id: 's-lf', name: 'Lucia Flores', initials: 'LF', location_id: 'loc-downtown', location_name: 'Downtown', units: 10, recorded: 7, worked: 7, worked_days_in_window: 9, lessons_completed_in_window: 8, recorded_days_in_window: 6, accuracy: 91, last_active_display: 'Today, 8:20 AM', excuses: [] },
  { server_id: 's-bl', name: 'Brandon Lee', initials: 'BL', location_id: 'loc-downtown', location_name: 'Downtown', units: 10, recorded: 8, worked: 8, worked_days_in_window: 10, lessons_completed_in_window: 10, recorded_days_in_window: 8, accuracy: 88, last_active_display: 'Today, 7:40 AM', excuses: [] },
  { server_id: 's-em', name: 'Emma Rocelis Amaya Perez', initials: 'EA', location_id: 'loc-downtown', location_name: 'Downtown', units: 10, recorded: 10, worked: 10, worked_days_in_window: 11, lessons_completed_in_window: 11, recorded_days_in_window: 10, accuracy: 93, last_active_display: 'Today, 8:05 AM', excuses: [] },
  // feat-105: the totals-basis reconciliation fixture (was the feat-084 "67%" row).
  // Under the new totals basis it reads 100% — the worked example in the spec
  // (3 lessons over 3 worked days, regardless of which day each landed). Its activity
  // timeline (FAKE_ACTIVITY['s-jr']) reconciles per ADR 0001 D3:
  //   • LESSON % (exact): Σ len(d.lessons) over ALL feed days (INCLUDING the non-worked
  //     day's unit, which now COUNTS) = 2 + 1 + 0 + 1 = 4 == lessons_completed_in_window.
  //   • denominator (exact): count of worked days = 3 == worked_days_in_window.
  //   • RECORDED % (feat-177, now EXACT): the 3 recorded days in the feed ARE
  //     recorded_days_in_window=3. It used to be 4 (a lower bound) because one worked
  //     day carried 2 recordings; under the day basis that day collapses to one, so
  //     the feed and the row agree at the same grain (AC6) instead of merely bounding.
  //   → lesson_pct = min(100, round(4/3*100)) = 100 (the multiset still overflows the
  //     cap — s-jr remains the cap fixture); recorded_pct = 3/3 = 100 with NO clamp.
  //     He is still Above & beyond, now on the LESSON numerator alone (4 > 3).
  { server_id: 's-jr', name: 'José Ramirez', initials: 'JR', location_id: 'loc-downtown', location_name: 'Downtown', units: 8, recorded: 8, worked: 8, worked_days_in_window: 3, lessons_completed_in_window: 4, recorded_days_in_window: 3, accuracy: 85, last_active_display: 'Today, 7:48 AM', excuses: [] },
  {
    server_id: 's-cr', name: 'Carlos Rivera', initials: 'CR', location_id: 'loc-airport', location_name: 'Airport', units: 10, recorded: 7, worked: 10, worked_days_in_window: 10, lessons_completed_in_window: 10, recorded_days_in_window: 6, accuracy: 82, last_active_display: 'Today, 7:55 AM',
    excuses: [
      { reason: 'Mic stopped working mid-shift', manager_name: 'Clayton', date: 'Jun 6, 2026', base_units: 10, base_recorded: 7, base_worked: 10 },
    ],
  },
  { server_id: 's-kp', name: 'Kevin Park', initials: 'KP', location_id: 'loc-airport', location_name: 'Airport', units: 6, recorded: 8, worked: 8, worked_days_in_window: 8, lessons_completed_in_window: 4, recorded_days_in_window: 8, accuracy: 79, last_active_display: 'Today, 8:02 AM', excuses: [] },
  {
    server_id: 's-at', name: 'Ana Torres', initials: 'AT', location_id: 'loc-airport', location_name: 'Airport', units: 9, recorded: 5, worked: 9, worked_days_in_window: 9, lessons_completed_in_window: 5, recorded_days_in_window: 4, accuracy: 74, last_active_display: 'Yesterday, 8:05 AM',
    excuses: [
      { reason: 'Left phone at home', manager_name: 'Steve', date: 'Jun 2, 2026', base_units: 7, base_recorded: 4, base_worked: 7 },
      { reason: 'Mic battery dead, couldn’t record', manager_name: 'Clayton', date: 'May 28, 2026', base_units: 5, base_recorded: 3, base_worked: 5 },
    ],
  },
  { server_id: 's-sm', name: 'Sofia Mendez', initials: 'SM', location_id: 'loc-airport', location_name: 'Airport', units: 5, recorded: 9, worked: 9, worked_days_in_window: 9, lessons_completed_in_window: 4, recorded_days_in_window: 9, accuracy: 68, last_active_display: '2 days ago', excuses: [] },
  // Yesenia: 2 lessons + 2 recordings over 8 worked days → 25% / 25% (the partial fixture).
  { server_id: 's-yv', name: 'Yesenia Vargas', initials: 'YV', location_id: 'loc-airport', location_name: 'Airport', units: 3, recorded: 2, worked: 8, worked_days_in_window: 8, lessons_completed_in_window: 2, recorded_days_in_window: 2, accuracy: 51, last_active_display: '5 days ago', excuses: [] },
  // Priya: 0 worked days in window → lesson_pct/recorded_pct null → both columns "—".
  { server_id: 's-pa', name: 'Priya Anand', initials: 'PA', location_id: 'loc-airport', location_name: 'Airport', units: 2, recorded: 1, worked: 3, worked_days_in_window: 0, lessons_completed_in_window: 0, recorded_days_in_window: 0, accuracy: 60, last_active_display: '3 weeks ago', excuses: [] },
  // feat-177 AC7(a)/AC3 — THE >100% FIXTURE. Noah records on days he was never on the
  // Square clock (no Square mapping for some of his work), so his recorded days are NOT
  // a subset of his worked days. This is the case that killed the "≤100% by construction"
  // premise, and Amendment 2 says the report must SHOW it rather than launder it:
  //   • RECORDED column = 12 over 9 worked days — the column exceeds Worked, on purpose.
  //   • RECORDED % = 8 recorded days over 6 worked days in-window = **133%**, UNCAPPED.
  //     133 is chosen deliberately over a value that lands on 100: an assertion pinned
  //     to 100 passes identically whether the cap was removed or not, so it could never
  //     prove the uncapping. 133 is a number the old clamp would have visibly mangled.
  //   • his activity feed (FAKE_ACTIVITY['s-nk']) reconciles exactly: 8 recorded days,
  //     6 worked days, three of the recorded ones off the clock.
  // Do NOT "fix" recorded > worked and do NOT reintroduce a cap on recorded_pct — that
  // inequality and that 133% ARE the fixture. LESSON % stays capped (see windowPct).
  { server_id: 's-nk', name: 'Noah Kim', initials: 'NK', location_id: 'loc-airport', location_name: 'Airport', units: 6, recorded: 12, worked: 9, worked_days_with_recording_all_time: 7, worked_days_in_window: 6, lessons_completed_in_window: 5, recorded_days_in_window: 8, accuracy: 77, last_active_display: 'Today, 8:15 AM', excuses: [] },
  // ── feat-195 D2: the EXHAUSTED cohort (units === FAKE_UNITS_TOTAL) ──────────────
  // These three are the reason the feature exists. Each is a member who has completed
  // the ENTIRE curriculum, so the app has stopped asking them for lessons — and under
  // the pre-feat-195 rule their windowed lesson count decayed to 0 and pinned them at
  // Behind permanently, with no action available to them. The carve-out silences the
  // lessons axis on BOTH sides and paces them on recordings alone (AC5/AC5b).
  //
  // Tomás — THE RESCUED ROW (AC5). 0 lessons in the window over 10 worked days:
  // on `develop` that is `0 < 9` → **Behind**. With the carve-out he is judged on
  // recordings alone (10 recorded / 10 worked) → **On track**. His `lesson_pct` is
  // null → "—" (D8) even though the ratio computes to 0, and the card says why.
  { server_id: 's-tv', name: 'Tomás Vega', initials: 'TV', location_id: 'loc-downtown', location_name: 'Downtown', units: 12, recorded: 12, worked: 12, worked_days_in_window: 10, lessons_completed_in_window: 0, recorded_days_in_window: 10, accuracy: 92, last_active_display: 'Today, 8:22 AM', excuses: [] },
  // Rosa — THE TRANSITIONAL MEMBER (QA's EXH_RECENT_NOT_ABOVE). She finished her last
  // units INSIDE the window, so she is exhausted AND carries a live, NON-ZERO windowed
  // lesson count: 3 lessons over 2 worked days, which is `3 > 2` → **Above** on
  // `develop`. The carve-out is two-sided, so lessons can no longer LIFT her either and
  // she reads **On track** on recordings (2/2). Her card's Lessons row renders "—"
  // (Preston's O1) beside a `not counted` note, hiding a real count of 3 — the one row
  // where that ruling has a cost (design brief §2.8). DO NOT seed exhausted members
  // with lessons_completed_in_window: 0 only — that fixture cannot exercise this half.
  { server_id: 's-rh', name: 'Rosa Herrera', initials: 'RH', location_id: 'loc-downtown', location_name: 'Downtown', units: 12, recorded: 4, worked: 4, worked_days_in_window: 2, lessons_completed_in_window: 3, recorded_days_in_window: 2, accuracy: 89, last_active_display: 'Today, 7:52 AM', excuses: [] },
  // Omar — EXHAUSTED + BEHIND (the composition, and the D3 payoff). Curriculum complete,
  // but he recorded on only 6 of 10 worked days → Behind ON RECORDINGS. The card leads
  // with the curriculum-complete clause and then names *recordings* explicitly, so the
  // coral pill can never be misread as punishing a finished curriculum. Proves the
  // carve-out is not an amnesty: an exhausted member is still fully judged on recordings.
  { server_id: 's-og', name: 'Omar Gil', initials: 'OG', location_id: 'loc-airport', location_name: 'Airport', units: 12, recorded: 8, worked: 11, worked_days_in_window: 10, lessons_completed_in_window: 0, recorded_days_in_window: 6, accuracy: 81, last_active_display: 'Yesterday, 7:31 AM', excuses: [] },
  // Elena — THE DOUBLE-CAUSE ROW: exhausted AND no shifts in the window. Both causes of
  // "—" are live at once, and the resolution is that the exhausted treatment does NOT
  // apply: `worked_days == 0` short-circuits BEFORE either axis is consulted, so the
  // carve-out never fired and nothing was "not counted". Her Lessons row therefore shows
  // its REAL value (2) with a `not scored` note — NOT a dash. Seeded because this is the
  // one card whose copy cannot explain a dash (its sentences deliberately omit "paced on
  // recordings alone" — nothing is being paced), so a `counted` regression shows up here
  // first and nowhere else on the roster.
  { server_id: 's-ed', name: 'Elena Duarte', initials: 'ED', location_id: 'loc-downtown', location_name: 'Downtown', units: 12, recorded: 6, worked: 7, worked_days_in_window: 0, lessons_completed_in_window: 2, recorded_days_in_window: 0, accuracy: 87, last_active_display: '3 weeks ago', excuses: [] },
];

/**
 * feat-105 (ADR 0001 D1/D2): the totals-basis percentage. Mirrors the backend's
 * single-source `_window_pct(numerator, worked_days)` — round, with a `null` sentinel
 * when there are no worked days. Fake mode is the backend stand-in, so this math lives
 * HERE, never in the AdherenceCell formatter.
 *
 * feat-177 — THE UNCAPPING TRAP. The cap is now per-caller, and the two callers differ:
 *   • RECORDED % is UNCAPPED. Recorded days are not a subset of Square worked days (a
 *     member can record on a day they were never on the clock), so >100% is a real,
 *     intended value — the report must say 133%, not launder it into 100%.
 *   • LESSON % stays CAPPED at 100. Its numerator is still a MULTISET (two units in one
 *     day count twice), so an uncapped lesson ratio would be meaningless, not truthful.
 * Uncapping both is the exact regression this split exists to prevent (AC3c).
 */
function windowPct(numerator: number, workedDays: number, cap: boolean): number | null {
  if (workedDays <= 0) return null;
  const raw = Math.round((numerator / workedDays) * 100);
  return cap ? Math.min(100, raw) : raw;
}

/**
 * feat-195 D2: the exhaustion predicate, mirroring the backend's `is_exhausted`.
 * ONE named place, so every fake fixture tests the SAME predicate rather than three
 * inline comparisons.
 *
 *   exhausted ⇔ units_total > 0 AND units >= units_total
 *
 * Two traps this signature exists to close:
 *  - `units_total === 0` (a tenant with no active curriculum) is NOT exhausted. An
 *    empty catalog means the curriculum was never SET UP, not that it was FINISHED,
 *    and silencing the lessons axis tenant-wide would hide a config problem behind a
 *    healthier-looking roster (spec, AC10).
 *  - Exhaustion is NEVER inferred from a zero lesson count. It is a statement about
 *    `units` vs `units_total`, and the two are INDEPENDENT — a member who finished
 *    their last units inside the window is exhausted with a non-zero windowed count
 *    (s-rh). It is also deliberately NOT windowed: completing the curriculum is a
 *    permanent fact and does not age out.
 */
function fakeIsExhausted(units: number, unitsTotal: number): boolean {
  return unitsTotal > 0 && units >= unitsTotal;
}

/**
 * feat-112 + feat-195: status over the ROLLING-14 WINDOW — mirrors backend
 * `evaluate_status` VERBATIM (the fake mode is the backend stand-in; the rule is
 * single-sourced there and re-stated here only so `npm run start:fake` matches prod).
 * Inputs are the windowed counts (`lessons_completed_in_window`,
 * `recorded_days_in_window`, `worked_days_in_window`), NOT the all-time totals —
 * status is now recoverable — plus the ALL-TIME exhaustion flag.
 *
 *   no_recent_shifts ← worked_days == 0 (short-circuit BEFORE the pace rule, which
 *                      would otherwise vacuously yield on_track for absent crew).
 *   Behind           ← recordings < worked_days-1, OR (NOT exhausted AND lessons <
 *                      worked_days-1) — ±1 grace: being exactly one short is forgiven.
 *   Above & beyond   ← NOT Behind AND (recordings > worked_days, OR (NOT exhausted AND
 *                      lessons > worked_days)) — the Above line is NOT graced.
 *   On track         ← otherwise.
 *
 * feat-195 D2 — THE CARVE-OUT IS TWO-SIDED, and suppressing only the Behind half is
 * the easy mistake. For an exhausted member the lessons axis goes quiet in BOTH
 * directions: it can no longer push them to Behind AND it can no longer lift them to
 * Above. An exhausted member with `recordings == worked_days` must read On track, and
 * one with `recordings > worked_days` must read Above (AC5b). `exhausted` is passed as
 * a named field rather than a fourth bare int so an argument-order slip in a signature
 * that already takes three ints in a row is not expressible.
 */
function fakeStatus(
  lessons: number,
  recordings: number,
  workedDays: number,
  opts: { exhausted: boolean },
): TrainingStatus {
  if (workedDays <= 0) return 'no_recent_shifts';
  const graceLine = workedDays - 1;
  if (recordings < graceLine) return 'behind';
  if (!opts.exhausted && lessons < graceLine) return 'behind';
  if (recordings > workedDays) return 'above';
  if (!opts.exhausted && lessons > workedDays) return 'above';
  return 'on_track';
}

// ---------- feat-195: the fake-mode status-reason payload ----------
//
// ⚠️ FU-1, STATED PLAINLY BECAUSE IT CANNOT BE TESTED AWAY (ADR 0001 §B6): every
// sentence below ALSO exists in Python, in `restaurant-backend`'s status-copy table,
// which is the AUTHORITATIVE copy per D6. Fake mode is a hand-written mirror, so the
// prose lives in two languages and **NO SINGLE TEST RUNNER CAN ASSERT THEY ARE EQUAL**
// — the frontend suite cannot import the Python constants and the backend suite cannot
// see these. The containment is exactly this: ONE named constant per side, each
// pointing at the other. The lockstep test (AC7) asserts the SHAPE — same metric keys
// in the same order, same flags, same triggered_by — which is what it can actually
// prove. **DO NOT read a green AC7 as proof that the two prose sets agree.** The fix
// that would delete the duplication (serving fake mode from a captured real backend
// response) is FOLLOWUP FU-1, not this feature.
//
// ⚠️ Every number below is interpolated HERE, in the fake-mode backend stand-in. This
// is the ONLY place in the frontend allowed to do threshold arithmetic (spec D6 / AC4).
// The real path renders `sentences[]` verbatim and never composes or reformats one.
//
// Cross-reference: restaurant-backend/routes/training_progress.py — the status copy
// table (design brief §2.1–§2.5). Keep the two in lockstep by hand.
const FAKE_STATUS_REASON_COPY = {
  labels: {
    worked_days: 'Days worked (Square)', // names the SYSTEM OF RECORD — a disputed
    // number is only actionable if you know where to dispute it (design O2).
    lessons: 'Lessons completed',
    recorded_days: 'Days recorded',
  },
  notes: {
    // The D2 carve-out fired: this axis was SILENCED. Pairs with `value: null` → "—".
    notCounted: 'not counted',
    // worked_days == 0: nothing was scored at all — the short-circuit precedes the
    // pace rule, so no axis was ever consulted.
    notScored: 'not scored',
    // worked_days == 1 → grace line 0. The axis IS counted and IS trivially cleared,
    // but "needs 0" reads as nonsense, so no numeric line is shown (design §2.7a).
    noMinimumYet: 'no minimum yet',
  },
  graceNote: '(1 day of grace is already included.)',
  noRecentShifts: [
    'No completed shifts on the Square clock in the last 2 weeks, so there is no pace to measure against.',
    // Reuses the roster footnote's own phrase word-for-word so the card and the
    // footnote agree. No grace note — no threshold was applied.
    'Set aside from the Behind / On track / Above counts until they are back on the clock.',
  ],
};

// Fixed fake window. Mirrors FAKE_ACTIVITY_WINDOW below (2026-06-08 inclusive →
// 2026-06-22 EXCLUSIVE, i.e. Jun 21 inclusive) so the card and the dialog's activity
// feed reconcile. `display` is a literal: the backend emits the whole header line
// precisely so `end_exclusive − 1 day` is never computed in TypeScript (ADR §B3).
const FAKE_STATUS_WINDOW: StatusReasonWindow = {
  start: '2026-06-08',
  end_exclusive: '2026-06-22',
  display: 'Last 14 days (Jun 8 – Jun 21)',
};

/** "1 day worked" / "10 days worked" — §2.7b: the naive template breaks at 1. */
function fakeDaysWorked(workedDays: number): string {
  return `${workedDays} ${workedDays === 1 ? 'day' : 'days'} worked`;
}

/**
 * Builds the fake-mode `StatusReason` in the IDENTICAL shape the backend emits
 * (ADR §B7: all five variants share ONE field set — there is no per-variant shape).
 * Mirrors `evaluate_status` + `build_status_reason`.
 */
function fakeStatusReason(
  inputs: { lessons: number; recorded: number; worked: number },
  status: TrainingStatus,
  opts: { exhausted: boolean; unitsTotal: number },
): StatusReason {
  const { exhausted, unitsTotal } = opts;
  const { worked, lessons, recorded } = inputs;
  const scored = worked > 0; // the pace rule ran at all
  const grace = worked - 1;
  const hasLine = scored && grace > 0; // "needs N" is displayable
  // THE CARVE-OUT FIRED — and it is `exhausted AND worked > 0`, never `exhausted`
  // alone. At worked == 0 the neutral short-circuit precedes the pace rule, so the
  // lessons axis was never CONSULTED, not SILENCED: nothing was "not counted", and
  // that row's copy deliberately omits "paced on recordings alone", so a "—" there
  // would be a dash no sentence could account for (design brief §2.5).
  const carveOutFired = exhausted && scored;

  const lessonsJudged = scored && !carveOutFired;
  const recordedJudged = scored;
  const lessonsBehind = lessonsJudged && hasLine && lessons < grace;
  const recordedBehind = recordedJudged && hasLine && recorded < grace;
  const lessonsAbove = lessonsJudged && lessons > worked;
  const recordedAbove = recordedJudged && recorded > worked;

  const metrics: StatusReasonMetric[] = [
    {
      // The denominator, not an axis: it is never judged, so it carries no line, no
      // verdict and no qualifier — and it is NOT dimmed (design mockup §1).
      key: 'worked_days',
      label: FAKE_STATUS_REASON_COPY.labels.worked_days,
      value: worked,
      threshold: null,
      meets: null,
      counted: true,
      note: null,
    },
    {
      key: 'lessons',
      label: FAKE_STATUS_REASON_COPY.labels.lessons,
      // O1 (Preston, twice): an exhausted member's Lessons row renders "—" EVERYWHERE,
      // including the transitional member whose true windowed count is non-zero. One
      // rule, no special cases — so the backend emits the thing that renders.
      value: carveOutFired ? null : lessons,
      threshold: lessonsJudged && hasLine ? grace : null,
      meets: lessonsJudged ? (hasLine ? lessons >= grace : true) : null,
      counted: !carveOutFired,
      note: carveOutFired
        ? FAKE_STATUS_REASON_COPY.notes.notCounted
        : !scored
          ? FAKE_STATUS_REASON_COPY.notes.notScored
          : !hasLine
            ? FAKE_STATUS_REASON_COPY.notes.noMinimumYet
            : null,
    },
    {
      key: 'recorded_days',
      label: FAKE_STATUS_REASON_COPY.labels.recorded_days,
      value: recorded,
      threshold: recordedJudged && hasLine ? grace : null,
      meets: recordedJudged ? (hasLine ? recorded >= grace : true) : null,
      counted: true,
      note: !scored
        ? FAKE_STATUS_REASON_COPY.notes.notScored
        : !hasLine
          ? FAKE_STATUS_REASON_COPY.notes.noMinimumYet
          : null,
    },
  ];

  const triggeredBy: string[] = [];
  if (status === 'behind') {
    if (lessonsBehind) triggeredBy.push('lessons');
    if (recordedBehind) triggeredBy.push('recorded_days');
  } else if (status === 'above') {
    if (lessonsAbove) triggeredBy.push('lessons');
    if (recordedAbove) triggeredBy.push('recorded_days');
  }

  const sentences: string[] = [];
  // Clause 0 — the curriculum-complete pair ALWAYS leads for an exhausted member whose
  // pace was actually measured. Naming {units_total} is deliberate: "All 12" reconciles
  // against a Lessons column already reading 12 of 12.
  if (carveOutFired) {
    sentences.push(
      unitsTotal === 1
        ? 'The one lesson in the curriculum is complete — nothing left to finish.'
        : `All ${unitsTotal} lessons in the curriculum are complete — nothing left to finish.`,
    );
    sentences.push("The lessons axis isn't counted; pace is measured on recordings alone.");
  }

  if (status === 'no_recent_shifts') {
    // `no_recent_shifts` LEADS — it is the actual verdict, and it is non-judgmental.
    sentences.push(...FAKE_STATUS_REASON_COPY.noRecentShifts);
    // The exhaustion fact MAY follow, stripped to a BARE STATEMENT: no "nothing left to
    // finish", and emphatically no "paced on recordings alone", because nothing is
    // being paced. It states a fact without claiming a verdict (ADR §B6b, design §2c).
    if (exhausted) {
      sentences.push(
        unitsTotal === 1
          ? 'The one lesson in the curriculum is complete — nothing left to finish.'
          : `All ${unitsTotal} lessons in the curriculum are complete — nothing left to finish.`,
      );
    }
  } else if (status === 'behind') {
    const both = lessonsBehind && recordedBehind;
    if (both) {
      sentences.push(
        `Behind because lessons completed (${lessons}) and days recorded (${recorded}) are both below the ${grace} needed for ${fakeDaysWorked(worked)}.`,
      );
    } else if (lessonsBehind) {
      sentences.push(
        `Behind because lessons completed (${lessons}) is below the ${grace} needed for ${fakeDaysWorked(worked)}.`,
      );
    } else {
      sentences.push(
        `Behind because days recorded (${recorded}) is below the ${grace} needed for ${fakeDaysWorked(worked)}.`,
      );
    }
    sentences.push(FAKE_STATUS_REASON_COPY.graceNote);
  } else if (status === 'above') {
    if (lessonsAbove && recordedAbove) {
      sentences.push(
        `Above & beyond because lessons completed (${lessons}) and days recorded (${recorded}) both exceed ${fakeDaysWorked(worked)} — by ${lessons - worked} and ${recorded - worked}.`,
      );
    } else if (lessonsAbove) {
      sentences.push(
        `Above & beyond because lessons completed (${lessons}) exceeds ${fakeDaysWorked(worked)} by ${lessons - worked}.`,
      );
    } else {
      sentences.push(
        `Above & beyond because days recorded (${recorded}) exceeds ${fakeDaysWorked(worked)} by ${recorded - worked}.`,
      );
    }
    // No grace note: the Above line is NOT graced.
  } else if (!hasLine) {
    // worked_days == 1 → grace line 0. "both meet the 0 needed for 1 days worked" is
    // absurd on its face and ungrammatical too, so this state gets its own sentence.
    sentences.push(
      'On track — with 1 day worked in the last 2 weeks, the grace day leaves no minimum to clear yet.',
    );
  } else if (carveOutFired) {
    sentences.push(
      `On track because days recorded (${recorded}) meets the ${grace} needed for ${fakeDaysWorked(worked)}.`,
    );
    sentences.push(FAKE_STATUS_REASON_COPY.graceNote);
  } else {
    sentences.push(
      `On track because lessons completed (${lessons}) and days recorded (${recorded}) both meet the ${grace} needed for ${fakeDaysWorked(worked)}.`,
    );
    sentences.push(FAKE_STATUS_REASON_COPY.graceNote);
  }

  return {
    status, // echo — exists to be CHECKED against row.status, not rendered (§B4)
    window: FAKE_STATUS_WINDOW,
    metrics,
    triggered_by: triggeredBy,
    grace_days: 1,
    lessons_exhausted: exhausted,
    sentences,
  };
}

function fakeRow(seed: FakeSeed): TrainingProgressRow {
  // feat-112: status is computed over the WINDOWED inputs (same window as lesson_pct/
  // recorded_pct), not the all-time units/recorded/worked — those still feed the count
  // columns only. worked_days_in_window === 0 ⇒ neutral "no_recent_shifts".
  // feat-195: exhaustion is the one status input that is deliberately NOT windowed.
  const exhausted = fakeIsExhausted(seed.units, FAKE_UNITS_TOTAL);
  const status = fakeStatus(
    seed.lessons_completed_in_window,
    seed.recorded_days_in_window,
    seed.worked_days_in_window,
    { exhausted },
  );
  return {
    server_id: seed.server_id,
    name: seed.name,
    initials: seed.initials,
    location_id: seed.location_id,
    location_name: seed.location_name,
    units: seed.units,
    units_total: FAKE_UNITS_TOTAL,
    recorded: seed.recorded,
    worked: seed.worked,
    days_worked_all_time: seed.worked, // mirror backend D2: `worked` IS the all-time day count
    worked_days_in_window: seed.worked_days_in_window,
    // feat-105: the backend (here, its fake stand-in) owns the cap+round+null sentinel.
    // feat-177: lessons CAPPED (multiset), recorded UNCAPPED (plain distinct days).
    // feat-195 D8: an exhausted member's Lesson % is null → "—". The ratio COMPUTES
    // (it is 0), but leaving 0% next to an On-track pill is the pill/column
    // contradiction D8 forbids, and 100% would be a lie about the window. `null` is
    // the honest third answer — *this ratio is not meaningful for this row* — and it
    // reuses the sentinel the AdherenceCell formatter already handles, so it needs
    // ZERO new TypeScript. `recorded_pct` is untouched.
    lesson_pct: exhausted
      ? null
      : windowPct(seed.lessons_completed_in_window, seed.worked_days_in_window, true),
    recorded_pct: windowPct(seed.recorded_days_in_window, seed.worked_days_in_window, false),
    lessons_completed_in_window: seed.lessons_completed_in_window,
    recorded_days_in_window: seed.recorded_days_in_window,
    // feat-084 Phase-2: all-time same-day coverage (status inputs). A same-day count can
    // never exceed distinct worked days, so the lesson side is still mocked as a cap.
    // feat-177: the RECORDING side stopped being a mock — it is now the summary tile's
    // numerator, so a seed may state it explicitly (see s-nk). min(recorded, worked) is
    // only the fallback, and it is wrong for anyone who records off the clock.
    worked_days_with_lesson_same_day_all_time: Math.min(seed.units, seed.worked),
    worked_days_with_recording_same_day_all_time:
      seed.worked_days_with_recording_all_time ?? Math.min(seed.recorded, seed.worked),
    accuracy: seed.accuracy,
    accuracy_display: `${seed.accuracy}%`,
    last_active: null,
    last_active_display: seed.last_active_display,
    status,
    // feat-195: built from the SAME evaluation that produced `status` above — the row
    // builder must never derive the verdict once and the reason separately, or the two
    // can drift and the card would explain a pill it does not match (ADR §A3/§B4).
    status_reason: fakeStatusReason(
      {
        lessons: seed.lessons_completed_in_window,
        recorded: seed.recorded_days_in_window,
        worked: seed.worked_days_in_window,
      },
      status,
      { exhausted, unitsTotal: FAKE_UNITS_TOTAL },
    ),
    times_excused: seed.excuses.length,
    excuse_history: seed.excuses,
    excuse_placeholder: true,
    excuse_data_source: 'seeded_excuse',
  };
}

/**
 * feat-195 AC7 — the lockstep seam. Evaluates the fake-mode mirror for ARBITRARY
 * windowed inputs (including `units_total` values fake mode never seeds, like a
 * single-unit or empty curriculum), so the lockstep test can drive this mirror with
 * the BACKEND's own captured cases rather than with inputs chosen to suit it.
 *
 * Exported for tests only — nothing in the app calls it. It exists because a mirror
 * tested solely against the fixtures it was written alongside proves very little: the
 * discriminating cases are the ones the backend chose.
 */
export function evaluateFakeStatusForTest(inputs: {
  units: number;
  units_total: number;
  lessons_completed_in_window: number;
  recorded_days_in_window: number;
  worked_days_in_window: number;
}): { status: TrainingStatus; lesson_pct: number | null; status_reason: StatusReason } {
  const exhausted = fakeIsExhausted(inputs.units, inputs.units_total);
  const windowed = {
    lessons: inputs.lessons_completed_in_window,
    recorded: inputs.recorded_days_in_window,
    worked: inputs.worked_days_in_window,
  };
  const status = fakeStatus(windowed.lessons, windowed.recorded, windowed.worked, { exhausted });
  return {
    status,
    lesson_pct: exhausted ? null : windowPct(windowed.lessons, windowed.worked, true),
    status_reason: fakeStatusReason(windowed, status, { exhausted, unitsTotal: inputs.units_total }),
  };
}

// `locationId` scopes the roster server-side in real mode; the fake response
// mirrors that scoping (filter + recompute the summary over the scoped rows) so
// the location filter is demonstrable in `npm run start:fake` and the tiles
// re-scope honestly — never recomputed in React (feedback_metrics_single_source).
function fakeResponse(locationId?: string): TrainingProgressResponse {
  const rows = FAKE_CREW.map(fakeRow).filter((r) => !locationId || r.location_id === locationId);
  let onTrack = 0;
  let behind = 0;
  let above = 0;
  let noRecentShifts = 0;
  let sumR = 0;
  let sumW = 0;
  rows.forEach((r) => {
    // feat-112: the three judgmental buckets EXCLUDE neutral crew (mirrors backend
    // _summarize). on_track = NOT behind AND NOT no_recent_shifts (still includes above).
    if (r.status === 'behind') behind += 1;
    else if (r.status === 'no_recent_shifts') noRecentShifts += 1;
    else onTrack += 1;
    if (r.status === 'above') above += 1;
    // feat-177 AC6: the tile numerator is sum(r.recorded) — plain distinct recorded
    // days, the SAME basis as the column, the % and the feed. An intersected numerator
    // was briefly specced and then rejected (Amendment 2): it would have zeroed out
    // servers with no Square mapping. Deliberately NO clamp here — the tile is allowed
    // to exceed 100%, exactly like the column.
    sumR += r.recorded;
    sumW += r.worked;
  });
  return {
    rows,
    summary: {
      on_track: onTrack,
      behind,
      above,
      no_recent_shifts: noRecentShifts,
      // feat-177: BOTH sides are day counts on the SAME all-time span — Σ distinct
      // recorded days over Σ distinct worked days. The tile label says "Days recorded
      // vs days worked"; nothing here is a shift count any more. NO clamp: if the crew
      // records on days they were never on the Square clock, this legitimately reads
      // over 100% and the page must render it (AC10).
      recorded_vs_worked_pct: sumW > 0 ? Math.round((100 * sumR) / sumW) : 0,
      crew_count: rows.length,
    },
    units_total: FAKE_UNITS_TOTAL,
    placeholder: false,
    data_source: 'real',
  };
}

// ---------- feat-095: fake-mode per-member activity timeline ----------
// Per-member map (server_id → ActivityDay[]) in the EXACT D1 contract shape so the
// dialog timeline renders under `npm run start:fake`. Dates are plausible recent
// ISO strings (fake mode has no real "today"; the timeline just renders them).
//
// The 's-jr' (José Ramirez) seed reconciles to his roster row under the feat-105
// TOTALS basis (ADR 0001 D3) — worked_days_in_window=3, lessons_completed_in_window=4:
//   • 3 worked days in-window: 06-20, 06-19, 06-18 (the EXACT denominator)
//   • units completed: 06-20 (2 units), 06-19 (1 unit), 06-18 (none),
//     PLUS one unit on a NON-worked day (06-15) — under the totals basis EVERY
//     in-window completion counts: Σ len(lessons) = 2+1+0+1 = 4 == lessons_completed_in_window.
//   • recorded days (feat-177, EXACT): 3 recorded days here == recorded_days_in_window=3.
//     The feed flag has always been a per-day boolean; the roster numerator is now the
//     same grain, so this reconciles exactly rather than as a lower bound (AC6).
// → lesson_pct = min(100, round(4/3*100)) = 100 (was 67% under the old same-day rule).
const FAKE_ACTIVITY: Record<string, ActivityDay[]> = {
  's-jr': [
    {
      date: '2026-06-20', worked: true, recorded: true,
      lessons: [
        { lesson_id: 'unit-a', title: 'Greeting Basics' },
        { lesson_id: 'unit-b', title: 'Upsell Drinks' },
      ],
    },
    {
      date: '2026-06-19', worked: true, recorded: true,
      lessons: [{ lesson_id: 'unit-c', title: 'Dessert Add-ons' }],
    },
    { date: '2026-06-18', worked: true, recorded: true, lessons: [] },
    {
      date: '2026-06-15', worked: false, recorded: false,
      lessons: [{ lesson_id: 'unit-d', title: 'Closing the Check' }],
    },
  ],
  // feat-177 AC3/AC7(a) — Noah's feed is where the >100% is legible day by day.
  // In-window: 6 worked days (06-20, 19, 18, 16, 15, 14) == worked_days_in_window.
  // Recorded days: 8 — including 06-17, 06-13 and 06-12, all OFF the Square clock
  // (worked:false, recorded:true) == recorded_days_in_window → 8/6 = **133%**.
  // The numerator exceeding the denominator is the point; a seed where every recorded
  // day is also a worked day cannot exercise the uncapped path at all.
  // Σ len(lessons) = 2 + 0 + 1 + 0 + 1 + 1 + 0 + 0 + 0 = 5 == lessons_completed_in_window.
  's-nk': [
    {
      date: '2026-06-20', worked: true, recorded: true,
      lessons: [
        { lesson_id: 'unit-a', title: 'Greeting Basics' },
        { lesson_id: 'unit-b', title: 'Upsell Drinks' },
      ],
    },
    { date: '2026-06-19', worked: true, recorded: true, lessons: [] },
    { date: '2026-06-18', worked: true, recorded: false, lessons: [{ lesson_id: 'unit-c', title: 'Dessert Add-ons' }] },
    // The off-clock recordings: they count everywhere — column, %, tile, feed — which
    // is what pushes the % past 100 (Amendment 2 chose this over intersecting them out).
    { date: '2026-06-17', worked: false, recorded: true, lessons: [] },
    { date: '2026-06-16', worked: true, recorded: true, lessons: [{ lesson_id: 'unit-d', title: 'Closing the Check' }] },
    { date: '2026-06-15', worked: true, recorded: true, lessons: [{ lesson_id: 'unit-e', title: 'Reading the Room' }] },
    { date: '2026-06-14', worked: true, recorded: true, lessons: [] },
    { date: '2026-06-13', worked: false, recorded: true, lessons: [] },
    { date: '2026-06-12', worked: false, recorded: true, lessons: [] },
  ],
  // A clean all-green member: a few worked+recorded+lesson days.
  's-dc': [
    {
      date: '2026-06-20', worked: true, recorded: true,
      lessons: [{ lesson_id: 'unit-a', title: 'Greeting Basics' }],
    },
    { date: '2026-06-19', worked: true, recorded: true, lessons: [{ lesson_id: 'unit-b', title: 'Upsell Drinks' }] },
    { date: '2026-06-18', worked: true, recorded: true, lessons: [] },
  ],
};

const FAKE_ACTIVITY_WINDOW = { start: '2026-06-08', end: '2026-06-22' };

function fakeActivity(serverId: string): TrainingActivityResponse {
  return {
    server_id: serverId,
    days: FAKE_ACTIVITY[serverId] ?? [],
    window_start: FAKE_ACTIVITY_WINDOW.start,
    window_end: FAKE_ACTIVITY_WINDOW.end,
  };
}

// ---------- Public API ----------

export interface TrainingProgressParams {
  locationId?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export async function getTrainingProgress(
  tenantId: string,
  params: TrainingProgressParams = {},
): Promise<TrainingProgressResponse> {
  if (isFake()) {
    return fakeResponse(params.locationId);
  }
  const qs = new URLSearchParams();
  if (params.locationId) qs.set('location_id', params.locationId);
  if (params.startDate) qs.set('start_date', params.startDate);
  if (params.endDate) qs.set('end_date', params.endDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<TrainingProgressResponse>(
    `/api/training-progress/${encodeURIComponent(tenantId)}${suffix}`,
  );
}

/**
 * feat-095: per-member recent-activity timeline (ADR 0017 D1). Hit lazily when the
 * crew-member dialog opens. On 404/403 throws TrainingProgressForbiddenError — the
 * dialog swallows it so the rest of the dialog still renders (graceful degrade, AC#7).
 * The frontend renders the backend flags/titles verbatim — no bucketing or %/day
 * math (feedback_metrics_single_source).
 */
export async function getTrainingActivity(
  tenantId: string,
  serverId: string,
): Promise<TrainingActivityResponse> {
  if (isFake()) {
    return fakeActivity(serverId);
  }
  return api<TrainingActivityResponse>(
    `/api/training-progress/${encodeURIComponent(tenantId)}/${encodeURIComponent(serverId)}/activity`,
  );
}
